import { useState, useEffect, useContext, useRef } from 'react';
import classNames from 'classnames';
import { Radio } from '@rmwc/radio';
import { TextFieldHelperText } from '@rmwc/textfield';
import { FormField } from '@rmwc/formfield';
import { updateField } from '../../actions/actionCreators';
import errorMessage from '../../utils/errorMessage';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { FieldsetValidatorContext } from './Form';
import { validators } from '../../utils/validators';

interface RadioFieldProps {
  name: string;
  form: string;
  options: { label: string; value: string; logoURL?: string }[];
  required?: boolean;
  label?: string;
  alignEnd?: boolean;
  noLabel?: boolean;
  marginTop?: boolean;
  hidden?: boolean;
  onChange?: (string) => void;
}

const RadioField: React.FC<RadioFieldProps> = ({
  name,
  form,
  options = [],
  required,
  label,
  alignEnd,
  noLabel,
  marginTop,
  hidden,
  onChange,
}) => {
  const [error, setError] = useState(null);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const el = useRef<HTMLDivElement>(null);
  const value = useAppSelector(
    (state) => state.forms[form]?.[name]?.value || ''
  );
  const dispatch = useAppDispatch();
  const fieldsetValidator = useContext(FieldsetValidatorContext);

  const validate = () => {
    const errorMessages = errorMessage(
      { name, label, required },
      validators.required(value)
    );
    if (errorMessages) {
      setError(errorMessages);
      return false;
    }

    setError(null);
    return true;
  };

  useEffect(() => {
    const valid = validate();

    if (el.current) {
      fieldsetValidator.register({
        name,
        required,
        el: el.current,
        valid,
      });
    }

    return () => fieldsetValidator.unregister(name);
  }, [el, value]);

  const handleChange = (e) => {
    if (onChange) onChange(e.target.value);

    setDisplayError(true);

    const action = updateField(form, name, e.target.value);
    dispatch(action);
  };

  const hasOptions = options && options.length > 0;

  const handleBlur = () => {
    setDisplayError(true);
    validate();
  };

  return (
    <>
      {label && !noLabel && !hidden && (
        <h3
          className={classNames('step__heading', {
            'mt--0': true,
            'mt--7': marginTop,
          })}
        >
          {required && label ? `${label} *` : label}
        </h3>
      )}
      <div ref={el}>
        {hasOptions &&
          options.map((option) => (
            <FormField
              key={option.value}
              className={classNames('mdc-form-field--radio', {
                'mdc-form-field--error': error,
                'mdc-form-field--hidden': hidden,
              })}
              alignEnd={alignEnd}
            >
              <Radio
                name={name}
                label={option.logoURL ? '' : option.label}
                checked={option.value === value}
                value={option.value}
                onBlur={handleBlur}
                onChange={handleChange}
                required={required}
              >
                {option.logoURL && (
                  <img src={option.logoURL} alt={option.label} />
                )}
              </Radio>
            </FormField>
          ))}
      </div>
      {displayError && error && (
        <TextFieldHelperText persistent validationMsg>
          {error}
        </TextFieldHelperText>
      )}
    </>
  );
};

export default RadioField;
